import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../../types/modal.types';

import { useRunTwispTimeline } from '../../../__generated__/rest/api';
import { createFormComponent } from '../../utils/create-form-component';

const schema = z.object({
  amountCents: z.number().min(1, 'Amount is required'),
  plaidAccountId: z.string(),
});

export const DisbursementComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();

  const runTimelineMutation = useRunTwispTimeline();

  const handleSubmit = (data: z.infer<typeof schema>) => {
    const plaidAccountId = data.plaidAccountId.trim() || undefined;

    void handleMutation({
      mutation: () =>
        runTimelineMutation.mutateAsync({
          data: {
            input: {
              loanId,
              actions: [
                {
                  kind: 'DRAWDOWN' as const,
                  date: new Date().toISOString(),
                  drawdownAmountCents: data.amountCents,
                  plaidAccountId,
                },
              ],
            },
          },
        }),
      extractSuccess: (result) => !!result?.jobId,
      onSuccess: () => api.close(),
      debugLabel: 'disbursement',
      toast: {
        success: 'Disbursement created successfully',
        error: 'Failed to create disbursement',
        loading: 'Processing disbursement...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      {
        name: 'amountCents',
        label: 'Amount',
        placeholder: 'Enter amount',
        required: true,
        defaultValue: 0,
        component: CurrencyField,
        componentProps: {
          showCurrencySymbol: true,
        },
      },
      {
        name: 'plaidAccountId',
        label: 'Funding Account ID',
        type: 'text',
        placeholder: 'Plaid account id',
        helperText: 'Leave blank for a ledger-only drawdown.',
      },
    ],
    schema,
    defaultValues: {
      amountCents: 0,
      plaidAccountId: '',
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading: runTimelineMutation.isPending,
  });
};
